import type { Evaluation } from '@compass/client/types/evaluation';
import type { FeedbackInfo } from '@compass/client/types/feedback';
import type { Scorecard } from '@compass/client/types/scorecard';
import { PanelRightClose, PanelRightOpen } from 'lucide-react';
import React, { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { cn } from '@/utils';

import { getFeedbackReviewItems } from '../qcFeedbackReviews.utils';
import { QcFeedbackReviewsCountBadge } from './QcFeedbackReviewsCountBadge';
import { QcFeedbackReviewsFeed } from './QcFeedbackReviewsFeed';

interface QcFeedbackReviewsColumnProps {
  evaluation: Evaluation;
  feedback: FeedbackInfo[] | undefined;
  feedbackError?: boolean;
  feedbackLoading?: boolean;
  isCollapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  scorecard?: Scorecard | null;
  userNameById?: Map<string, string>;
}

export const QcFeedbackReviewsColumn: React.FC<QcFeedbackReviewsColumnProps> = ({
  evaluation,
  feedback,
  feedbackError = false,
  feedbackLoading = false,
  isCollapsed,
  onCollapsedChange,
  scorecard,
  userNameById,
}) => {
  const items = useMemo(
    () => getFeedbackReviewItems(evaluation, feedback, scorecard),
    [evaluation, feedback, scorecard],
  );
  const itemCount = items.length;

  return (
    <div
      className={cn(
        'shrink-0 border-l flex transition-all duration-200',
        isCollapsed ? 'w-10' : 'w-[388px]',
      )}
    >
      {isCollapsed ? (
        <div className="flex w-10 flex-col items-center py-3">
          <SimpleTooltip content="Expand Feedbacks" side="left">
            <Button
              aria-label="Expand Feedbacks"
              variant="ghost"
              size="sm"
              onClick={() => onCollapsedChange(false)}
              className="h-6 w-6 p-0"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
          </SimpleTooltip>
          <span className="sr-only">Feedbacks</span>
          <span
            aria-hidden="true"
            className="mt-2 text-xs text-muted-foreground [writing-mode:vertical-lr] rotate-180"
          >
            Feedbacks
          </span>
          <QcFeedbackReviewsCountBadge count={itemCount} className="mt-3" />
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col bg-background">
          <QcFeedbackReviewsFeed
            feedbackError={feedbackError}
            feedbackLoading={feedbackLoading}
            items={items}
            userNameById={userNameById}
            headerAction={
              <SimpleTooltip content="Collapse Feedbacks" side="bottom">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCollapsedChange(true)}
                  className="h-6 w-6 p-0"
                >
                  <PanelRightOpen className="h-4 w-4" />
                </Button>
              </SimpleTooltip>
            }
          />
        </div>
      )}
    </div>
  );
};
